import React, { useState, useEffect } from "react";
import { DateInput as PPReactDateInput, Calendar, Button, Row, Col } from "@paypalcorp/pp-react";
import { getMonthDayYear, getLocalTimeZone } from '../dateUtils';
import { parseMessages } from '../../utilityHelper';
import { get } from 'lodash';
const dateRangeMsg = parseMessages("common/DateRange");
const lang = document.getElementsByTagName('html')[0].getAttribute('lang');
const DateRange = (props) => {
    const landingPageDetails = JSON.parse(document.getElementById('data-page-details') && document.getElementById('data-page-details').innerHTML || '{}');
    const monthNamesObj = landingPageDetails.worldReadyData.monthNames;
    const monthNames = Object.values(monthNamesObj);
    const dayNames = landingPageDetails.worldReadyData.dayNames;
    const dayObj = {
        sun: dayNames[0],
        mon: dayNames[1],
        tue: dayNames[2],
        wed: dayNames[3],
        thu: dayNames[4],
        fri: dayNames[5],
        sat: dayNames[6],
    }
    let dateFromTo = new Date();
    let { month: dateToMonth, day: dateToDay, year: dateToYear } = getMonthDayYear(dateFromTo);
    dateFromTo.setTime(dateFromTo.getTime() - ((24 * 60 * 60 * 1000) * props.defaultDateRange));
    let { month: dateFromMonth, day: dateFromDay, year: dateFromYear } = getMonthDayYear(dateFromTo);

    const [startDate, setStartDate] = useState(new Date(dateFromYear, dateFromMonth - 1, dateFromDay));
    const [endDate, setEndDate] = useState(new Date(dateToYear, dateToMonth - 1, dateToDay));
    const [isStartDateCalendarVisible, showStartDateCalendar] = useState(false);
    const [isEndDateCalendarVisible, showEndDateCalendar] = useState(false);

    useEffect(() => {
        let dateFrom = props.dateFrom ? getLocalTimeZone(new Date(props.dateFrom)) : new Date(dateFromYear, dateFromMonth - 1, dateFromDay);
        let dateTo = props.dateTo ? getLocalTimeZone(new Date(props.dateTo)) : new Date(dateToYear, dateToMonth - 1, dateToDay);
        setStartDate(dateFrom);
        setEndDate(dateTo);
    },[props.dateFrom, props.dateTo]);


    // to set 'From' date and show the 'To' date calander
    const handleStartDateSelection = date => {
        const isStartDateSet =
            date && !startDate && !endDate && isStartDateCalendarVisible;

        setStartDate(date);
        showStartDateCalendar(false);
        if (isStartDateSet) {
            setTimeout(() => showEndDateCalendar(true)); // auto open end date calendar after start date is set
        }
    }
    // to set 'To' date and close the 'To' date calander
    const handleEndDateSelection = date => {
        setEndDate(date);
        showEndDateCalendar(false);
    }

    // to disable the dates in 'To' date calander with props.defaultDateRange value based on 'From' date selection
    // if props.defaultDateRange = 30, only 30 days from the 'From' date will be enabled in To date calander 
    const isDateDisabled = date => {
        let dayfromStartDate = (new Date(startDate));
        dayfromStartDate.setDate(dayfromStartDate.getDate() + props.defaultDateRange);
        return date < startDate || date > dayfromStartDate;
    }

    // date pattern based on 'lang' attribute (en-US, de-DE) to be passed in maskOptions 
    const buildDatePatternInfo = () => {
        const parts = new Intl.DateTimeFormat(lang, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
        }).formatToParts(new Date());

        return parts.reduce((acc, {
            type, value
        }) => {
            switch (type) {
                case 'month':
                    acc.maskOptions.mask += 'm';
                    acc.formatPattern += 'MM';
                    break;
                case 'day':
                    acc.maskOptions.mask += 'd';
                    acc.formatPattern += 'DD';
                    break;
                case 'year':
                    acc.maskOptions.mask += 'y';
                    acc.formatPattern += 'YYYY';
                    break;
                default:
                    acc.maskOptions.mask += `\`${value}`;
                    acc.formatPattern += `${value}`;
            }

            return acc;
        }, {
            maskOptions: {
                mask: ''
            },
            formatPattern: ''
        });
    };

    const datePatternInfo = buildDatePatternInfo();

    // to format the date
    const formatDate = (date) => {
        if (!date) {
            return ''
        }

        return new Intl.DateTimeFormat(lang, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        }).format(date)
    }

    // this is required in Calander component
    const formatToParts = (date) => {
        const parts = new Intl.DateTimeFormat(lang, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        }).formatToParts(date)

        return parts.reduce((acc, { type, value }) => {
            if (type === 'month' || type === 'day' || type === 'year') {
                acc[type] = value
            }
            return acc
        }, {})
    }

    // to parse the date format which will return the seperator and parts
    const parseFormat = (format) => {
        var separator = format.match(/[.\/\-\s].*?/),
            parts = format.split(/\W+/);
        if (!separator || !parts || parts.length === 0) {
            throw new Error("Invalid date format.");
        }
        return { separator: separator, parts: parts };
    }

    const createDate = (parts, format) => {
        var val;
        let dateParts = {};
        if (parts.length === format.parts.length) {
            for (let i = 0, cnt = format.parts.length; i < cnt; i++) {
                val = parseInt(parts[i], 10) || 1;
                switch (format.parts[i]) {
                    case 'DD':
                    case 'D':
                    case 'dd':
                    case 'd':
                        dateParts['day'] = val;
                        break;
                    case 'MM':
                    case 'M':
                    case 'mm':
                    case 'm':
                        dateParts['month'] = (val - 1);
                        break;
                    case 'YY':
                    case 'yy':
                        dateParts['year'] = (2000 + val);
                        break;
                    case 'YYYY':
                    case 'yyyy':
                        dateParts['year'] = val;
                        break;
                }
            }
        }
        return dateParts;
    }

    // create date using seperator and date parts
    const parseDate = (str) => {
        if (!str) {
            return null
        }
        let format = parseFormat(datePatternInfo.formatPattern);
        const parts = str.split(format.separator);
        const dateParts = createDate(parts, format);
        return new Date(dateParts.year, dateParts.month, dateParts.day);
    }

    const rangeStartCalendarElm = (
        <Calendar
            selectedDate={startDate}
            range="start"
            rangeCompletionDate={endDate}
            onSelection={handleStartDateSelection}
            formatToParts={formatToParts}
            monthNames={monthNames}
            dayNames={dayObj}
        />
    )
    const rangeEndCalendarElm = (
        <Calendar
            selectedDate={endDate}
            range="end"
            rangeCompletionDate={startDate}
            onSelection={handleEndDateSelection}
            isDateDisabled={isDateDisabled}
            formatToParts={formatToParts}
            monthNames={monthNames}
            dayNames={dayObj}
        />
    )

    return (
        <Row>
            <Col>
                <PPReactDateInput
                    name="rangeStart"
                    label={get(dateRangeMsg, 'startdate')}
                    calendar={rangeStartCalendarElm}
                    maskOptions={datePatternInfo.maskOptions}
                    selectedDate={startDate}
                    onSelection={handleStartDateSelection}
                    isCalendarVisible={isStartDateCalendarVisible}
                    onShowCalendar={showStartDateCalendar}
                    formatDate={formatDate}
                    parseDate={parseDate}
                />
            </Col>
            <Col>
                <PPReactDateInput
                    name="rangeEnd"
                    label={get(dateRangeMsg, 'enddate')}
                    calendar={rangeEndCalendarElm}
                    selectedDate={endDate}
                    maskOptions={datePatternInfo.maskOptions}
                    onSelection={handleEndDateSelection}
                    isCalendarVisible={isEndDateCalendarVisible}
                    onShowCalendar={showEndDateCalendar}
                    className="rangeEnd"
                    formatDate={formatDate}
                    parseDate={parseDate}
                />
            </Col>
        </Row>
    )
}
export default DateRange